import { useMutation } from '@tanstack/react-query';
import { useId, useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import styles from './TenantLifecyclePanel.module.scss';
import { updateTenantPilotEnd, type TenantStatusResult } from '../api/tenants.graphql';
import {
  bogotaDateInput,
  formatLifecycleDate,
  PILOT_END_RANGE,
  STATUS_REASON_LIMITS,
} from '../tenant-labels';

interface Props {
  tenant: { id: string; name: string; pilotEndsAt: string };
  onDone: (result: TenantStatusResult) => void;
  onCancel: () => void;
}

/**
 * OS-1720 — cambia la fecha de fin de un piloto vigente (extenderlo o acortarlo), con motivo. No
 * cambia el estado ni el acceso. El API valida el rango (mañana a 365 días, hora Colombia).
 */
export function PilotEndDialog({ tenant, onDone, onCancel }: Props): JSX.Element {
  const dateId = useId();
  const min = bogotaDateInput(PILOT_END_RANGE.minDays);
  const max = bogotaDateInput(PILOT_END_RANGE.maxDays);
  const [date, setDate] = useState('');
  const dateValid = date >= min && date <= max;
  const mutation = useMutation({
    mutationFn: (reason: string) => updateTenantPilotEnd(tenant.id, date, reason),
    onSuccess: onDone,
  });

  return (
    <ConfirmDialog
      title={`Cambiar fin del piloto: ${tenant.name}`}
      confirmLabel="Guardar fecha"
      cancelLabel="Volver"
      pending={mutation.isPending}
      confirmDisabled={!dateValid}
      error={mutation.error?.message ?? null}
      reason={{
        label: 'Motivo (queda en la auditoría)',
        placeholder: 'Ej.: el cliente pidió dos semanas más para evaluar',
        ...STATUS_REASON_LIMITS,
      }}
      onConfirm={(reason) => mutation.mutate(reason)}
      onCancel={onCancel}
    >
      <p>
        Fin actual del piloto: {formatLifecycleDate(tenant.pilotEndsAt)}. Vencer no suspende al
        tenant: solo genera alertas.
      </p>
      <label className={styles.dateField} htmlFor={dateId}>
        Nueva fecha de fin *
        <input
          id={dateId}
          type="date"
          min={min}
          max={max}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <span className={styles.hint}>Termina al final de ese día (hora Colombia).</span>
      </label>
    </ConfirmDialog>
  );
}
